import { Icon } from '@uag/react-core';
import { memo, PointerEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { ArticleLifeCycleIcon } from 'app/modules/wallforming/components/catalogue-container/ArticleLifeCycleIcon';
import { handleArticleRowBackground } from 'app/modules/wallforming/components/catalogue-container/utils/articleFilterFunctions';
import { isFramiClamp } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { handleOnInsertionStart } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { ArticleModel } from 'api/models';

interface ArticleTableRowProps {
    articleModel: ArticleModel;
}

export const ArticleTableRow = memo(({ articleModel }: ArticleTableRowProps) => {
    const { t } = useTranslation();

    const articleRowRef = useRef<HTMLDivElement | null>(null);
    const selectedArticleIdRef = useRef<string | undefined>(usePlacementStore.getState().selectedArticleId);

    const { setSelectedArticleId, setIsDragNewArticleStartRef } = usePlacementStore(
        useShallow((state) => ({
            setSelectedArticleId: state.setSelectedArticleId,
            setIsDragNewArticleStartRef: state.setIsDragNewArticleStartRef
        }))
    );

    useEffect(() => {
        const subscribedSelectedArticleId = usePlacementStore.subscribe(
            (state) => state.selectedArticleId,
            (newSelectedArticleId) => {
                const isSelected = articleModel.id === newSelectedArticleId;
                handleArticleRowBackground(articleRowRef, isSelected);
                if (isSelected) {
                    articleRowRef.current?.scrollIntoView({ block: 'nearest' });
                }
                selectedArticleIdRef.current = newSelectedArticleId;
            }
        );

        return () => {
            subscribedSelectedArticleId();
        };
    }, [articleModel.id]);

    const handleOnArticleRowSelection = (event?: React.PointerEvent): void => {
        if (selectedArticleIdRef.current !== articleModel.id) {
            setSelectedArticleId(articleModel.id);
            handleArticleRowBackground(articleRowRef, true);
        }

        if (!isFramiClamp(articleModel.articleNumber)) {
            setIsDragNewArticleStartRef(true);
        }

        document.body.style.cursor = 'move';
        event?.stopPropagation();
    };

    const handleOnDragStart = (dragEvent: React.DragEvent): void => {
        if ((dragEvent.buttons & 1) === 0) {
            return;
        }

        handleOnArticleRowSelection();
        handleOnInsertionStart(false, articleModel.id, t);
        dragEvent.preventDefault();
    };

    const handleOnDoubleClick = (mouseEvent: React.MouseEvent): void => {
        if (mouseEvent.button !== 0) {
            return;
        }

        handleOnArticleRowSelection();
        handleOnInsertionStart(true, articleModel.id, t);
    };

    const handleArticleRowRef = (articleRow: HTMLDivElement | null): void => {
        if (articleRow) {
            articleRowRef.current = articleRow;
            handleArticleRowBackground(articleRowRef, articleModel.id === selectedArticleIdRef.current);
        }
    };

    const handleOnPointerOver = (event: PointerEvent<HTMLDivElement>): void => {
        handleArticleRowBackground(articleRowRef, true);
        document.body.style.cursor = articleModel.id === selectedArticleIdRef.current ? 'move' : 'pointer';
        event.stopPropagation();
    };

    const handleOnPointerLeave = (): void => {
        handleArticleRowBackground(articleRowRef, articleModel.id === selectedArticleIdRef.current);
    };

    return (
        <div
            key={articleModel.id}
            ref={handleArticleRowRef}
            className="flex items-center justify-stretch h-full text-[12.5px] scroll-mt-8"
            data-catalogue-article-id={articleModel.id}
            data-testid={`catalogue-article-row-${articleModel.id}`}
            draggable="true"
            onDoubleClick={handleOnDoubleClick}
            onDragStart={handleOnDragStart}
            onPointerDown={handleOnArticleRowSelection}
            onPointerLeave={handleOnPointerLeave}
            onPointerOver={handleOnPointerOver}
        >
            <div className="w-[13px] font-['Consolas',monospace] cursor-move flex items-center justify-center h-full">
                <Icon icon="keyboard_arrow_left" size="lg" />
            </div>
            <div className="ml-0.5 pl-1 w-20 font-['Consolas',monospace] h-full flex items-center">
                {articleModel.articleNumber}
            </div>
            <div className="w-[49px] font-['Consolas',monospace] h-full flex items-center pl-[9px]">
                <ArticleLifeCycleIcon lifeCycleCode={articleModel.lifeCycleCode} />
            </div>
            <div className="flex items-center justify-start text-nowrap h-full">{articleModel.displayName}</div>
        </div>
    );
});

ArticleTableRow.displayName = 'ArticleTableRow';
